import { selectCurrentHotelId, selectCurrentHotelName } from 'accessors'
import { PrimaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import Dialog from 'components/ReusableComponents/Dialog'
import FileIcon from 'components/ReusableComponents/FileIcon'
import {
  SequenceCloseIcon,
  SequenceEmailIcon,
  SequenceSaveIcon,
  SequenceSmsIcon
} from 'features/CustomCampaigns/EditCampaign/CampaignFlowIcons'
import {
  dripMediumOptionsWithEmail,
  dripMediumOptionsWithText
} from 'features/CustomCampaigns/EditCampaign/CampaignSetup'
import CampaignEmailCustomizationSection from 'features/EmailEditor/CampaignEmailCustomizationSection'
import {
  EmailEditorProvider,
  useEmailEditor
} from 'features/EmailEditor/EmailEditorContext'
import EmailPreview from 'features/EmailEditor/EmailPreview'
import TestEmail from 'features/EmailEditor/TestEmail'
import TestSms from 'features/EmailEditor/TestSms'
import { TextPreview, TextPreviewBubble } from 'features/ManageCampaigns/TextPreview'
import {
  getExtensionFromUrl,
  getImgUrlFromCDN,
  isCampaignImageVisible
} from 'features/ManageCampaigns/utils/campaignHelpers'
import { getRequiredTagIdsForCustomCampaign } from 'features/ManageCampaigns/utils/campaignTagConfig'
import { mediaTags } from 'features/ManageCampaigns/utils/constants'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import styles from './StepContentEditor.module.scss'
import StepTextEditor from './StepTextEditor'

export const stepEditorChannels = {
  text: 'text',
  email: 'email'
}

const mediaTag = mediaTags.campaign

const urlRegex = /^(http|https)?:\/\/\S+/i

function StepContentEditorInner({
  title,
  medium,
  contentId,
  initialChannel,
  initialData,
  isCustomUrlAllowed = false,
  onClose,
  onSave,
  onSelectTemplate,
  onPersistDraft
}) {
  const hasText = dripMediumOptionsWithText.includes(medium)
  const hasEmail = dripMediumOptionsWithEmail.includes(medium)
  const isDualChannel = hasText && hasEmail

  const [activeChannel, setActiveChannel] = useState(
    initialChannel && (initialChannel === stepEditorChannels.email ? hasEmail : hasText)
      ? initialChannel
      : hasText
      ? stepEditorChannels.text
      : stepEditorChannels.email
  )

  const [messageTemplate, setMessageTemplate] = useState(
    initialData?.messageTemplate ?? ''
  )
  const [messageMedia, setMessageMedia] = useState(initialData?.messageMedia ?? null)
  const [customUrl, setCustomUrl] = useState(initialData?.customUrl ?? null)
  const [usedAllTags, setUsedAllTags] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  const userName = useSelector(s => s.login.currentUser.name)
  const hotelName = useSelector(selectCurrentHotelName)
  const hotelId = useSelector(selectCurrentHotelId)
  const requiredTagIds = getRequiredTagIdsForCustomCampaign(hotelId)

  const { saveChanges, hasUnsavedChanges } = useEmailEditor()

  const isEmailChannel = activeChannel === stepEditorChannels.email
  const hasEmailTemplate = Boolean(initialData?.emailTemplateId)

  const isTextContentValid =
    usedAllTags &&
    Boolean(messageTemplate.trim()) &&
    (customUrl ? Boolean(customUrl.match(urlRegex)) : true)
  const isSaveDisabled = isSaving || (!isEmailChannel && !isTextContentValid)

  function handleTextChange(text) {
    setMessageTemplate(text.replace(/inviteLink__\)(\S)/, 'inviteLink__) $1'))
  }

  function buildPayload() {
    return {
      messageTemplate,
      messageMedia,
      customUrl,
      isTextContentReviewed: hasText ? true : initialData?.isTextContentReviewed,
      isEmailContentReviewed:
        isEmailChannel && hasEmailTemplate ? true : initialData?.isEmailContentReviewed
    }
  }

  async function handleSave() {
    setIsSaving(true)

    try {
      if (isEmailChannel && hasEmailTemplate && hasUnsavedChanges) {
        await saveChanges()
      }

      onSave?.(buildPayload())
    } finally {
      setIsSaving(false)
    }
  }

  const handlePersistDraftBeforeTest = onPersistDraft
    ? async () => {
        await onPersistDraft(buildPayload())
      }
    : null

  return (
    <Dialog verticalPadding={20}>
      <div className={styles.editorShell}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.editingLabel}>Editing</span>
            <h2 className={styles.title}>{title}</h2>
          </div>
          <div className={styles.headerRight}>
            {isDualChannel && (
              <div className={styles.channelToggle} role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isEmailChannel}
                  className={
                    isEmailChannel
                      ? styles.channelToggleItem
                      : styles.channelToggleItemActive
                  }
                  onClick={() => setActiveChannel(stepEditorChannels.text)}
                >
                  <SequenceSmsIcon />
                  Text
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={isEmailChannel}
                  className={
                    isEmailChannel
                      ? styles.channelToggleItemActive
                      : styles.channelToggleItem
                  }
                  onClick={() => setActiveChannel(stepEditorChannels.email)}
                >
                  <SequenceEmailIcon />
                  Email
                </button>
              </div>
            )}
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close editor"
            >
              <SequenceCloseIcon />
            </button>
          </div>
        </header>

        {isEmailChannel && !hasEmailTemplate ? (
          <div className={styles.templateMissing}>
            <div className={styles.templateMissingCard}>
              <span className={styles.templateMissingIcon} aria-hidden="true">
                <SequenceEmailIcon />
              </span>
              <strong className={styles.templateMissingTitle}>
                No email template selected yet
              </strong>
              <span className={styles.templateMissingDescription}>
                Please select a template to preview and edit your email.
              </span>
              <PrimaryRegularButton onClick={onSelectTemplate}>
                Select a template
              </PrimaryRegularButton>
            </div>
          </div>
        ) : (
          <div className={styles.body}>
            <div className={styles.editorPanel}>
              <div className={styles.editorScroll}>
                {isEmailChannel ? (
                  <CampaignEmailCustomizationSection
                    includeInboxFields
                    className={styles.emailFields}
                  />
                ) : (
                  <StepTextEditor
                    messageTemplate={messageTemplate}
                    onTextChange={handleTextChange}
                    messageMedia={messageMedia}
                    setMessageMedia={setMessageMedia}
                    usedAllTags={usedAllTags}
                    setUsedAllTags={setUsedAllTags}
                    mediaTag={mediaTag}
                    requiredTagIds={requiredTagIds}
                    customUrl={customUrl}
                    setCustomUrl={setCustomUrl}
                    isCustomUrlAllowed={isCustomUrlAllowed}
                  />
                )}
              </div>
              <div className={styles.editorFooter}>
                <PrimaryRegularButton
                  className={styles.saveButton}
                  onClick={handleSave}
                  disabled={isSaveDisabled}
                >
                  <SequenceSaveIcon className={styles.saveButtonIcon} />
                  {isSaving ? 'Saving...' : 'Save changes & continue'}
                </PrimaryRegularButton>
              </div>
            </div>

            <div className={styles.previewPanel}>
              <div className={styles.previewHeader}>
                <span className={styles.previewDot} aria-hidden="true" />
                Previewing {title.toLowerCase()}
              </div>
              <div className={styles.previewBody}>
                {isEmailChannel ? (
                  <EmailPreview
                    isEditable={false}
                    holderClassName={styles.emailPreviewHolder}
                    skipInitialSectionReset
                    hideEditContentButton
                  />
                ) : (
                  <TextPreview className={styles.phonePreview}>
                    <>
                      <TextPreviewBubble
                        defaultData={{ userName, hotelName }}
                        hasLink
                        customLink={customUrl}
                        text={messageTemplate}
                      />
                      {messageMedia && isCampaignImageVisible(messageMedia) && (
                        <img
                          src={getImgUrlFromCDN(messageMedia, mediaTag, hotelId)}
                          alt="campaign media"
                          className={styles.previewMedia}
                        />
                      )}
                      {messageMedia && getExtensionFromUrl(messageMedia) === 'pdf' && (
                        <div className={styles.previewFile}>
                          <FileIcon extension="pdf" />
                          <span />
                        </div>
                      )}
                    </>
                  </TextPreview>
                )}
              </div>
              {isEmailChannel && (
                <div className={styles.previewFooter}>
                  <TestEmail />
                </div>
              )}
              {!isEmailChannel && contentId && (
                <div className={styles.previewFooter}>
                  <TestSms
                    contentCategory="Custom"
                    contentId={contentId}
                    onBeforeSend={handlePersistDraftBeforeTest}
                    restrictToInternalAdmin={false}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  )
}

function StepContentEditor({ contentId, ...props }) {
  return (
    <EmailEditorProvider
      contentId={contentId}
      contentCategory="Custom"
      mode="campaignBody"
    >
      <StepContentEditorInner contentId={contentId} {...props} />
    </EmailEditorProvider>
  )
}

export default StepContentEditor
